import React, { useEffect } from 'react';
import { Route, Redirect, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { toast } from 'react-toastify';
import useInvRole from '../hooks/useInvRole';
import { moduleLandingPath } from '../navigation';

import { IPD_COLORS } from '../theme';

/**
 * Permission-gated route for the Inventory Suite.
 *
 * Two tiers compose here:
 *   moduleKey  the workspace entitlement (Inventory_dashboard / Material_dashboard /
 *              Pharmacy_dashboard) — can the user enter this module at all
 *   action     the specific permission for this screen
 *
 * Stock and Material screens carry both. Pharmacy carries only `moduleKey`, because
 * its 35 action permissions are not wired this cycle (`rbac spec.md` §5) — without
 * the module gate it would be completely open.
 *
 * Must be a direct child of <Switch>, and `path`/`exact` must reach the underlying
 * <Route> through `...rest` — Switch reads child.props.path to pick a match without
 * ever rendering the child.
 *
 * This is also where the loading window is absorbed: while permissions resolve we
 * render a spinner instead of the screen, so every component below a guarded route
 * can assume `can()` is settled and no button flashes in then out.
 */
function AccessDenied({ to }) {
  // Toasting during render double-fires under StrictMode; Redirect must stay pure.
  useEffect(() => {
    toast.error('You do not have access to that screen.', { toastId: 'inv-access-denied' });
  }, []);

  return <Redirect to={to} />;
}

function RouteSpinner() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
      <CircularProgress sx={{ color: IPD_COLORS.primary }} />
    </Box>
  );
}

export default function InvRoleRoute({ action, moduleKey, component: Component, children, ...rest }) {
  const { loading, can, modules, homePath } = useInvRole();
  const { pathname } = useLocation();

  return (
    <Route
      {...rest}
      render={(props) => {
        if (loading) return <RouteSpinner />;

        const inModule = moduleKey ? modules[moduleKey] : true;
        if (!inModule || (action && !can(action))) {
          if (pathname === homePath) return null;
          return <AccessDenied to={homePath} />;
        }

        return Component ? <Component {...props} /> : children;
      }}
    />
  );
}

/**
 * The module base route — `/inventory-suite/stock` and `/inventory-suite/material`.
 *
 * Same two tiers as InvRoleRoute, but a missing *overview* permission is not treated
 * as a denial: the module entitlement is what grants entry, so a user who holds it
 * is redirected silently to the first screen they can open (moduleLandingPath) rather
 * than toasted and thrown out. They only get the denial when the entitlement itself
 * is missing, or when the workspace has nothing they may open at all.
 *
 * Pharmacy deliberately stays on InvRoleRoute — its whole subtree is one guard, so it
 * has no second screen to fall back to (see NAV_MODULES.pharmacy `entryAction`).
 */
export function InvModuleHomeRoute({ action, moduleKey, component: Component, ...rest }) {
  const { loading, can, modules, homePath } = useInvRole();
  const { pathname } = useLocation();

  return (
    <Route
      {...rest}
      render={(props) => {
        if (loading) return <RouteSpinner />;

        if (modules[moduleKey]) {
          if (can(action)) return <Component {...props} />;

          // Entering a workspace you hold is not a denial — no toast on this path.
          const landing = moduleLandingPath(moduleKey, can);
          if (landing && landing !== pathname) return <Redirect to={landing} />;
        }

        if (pathname === homePath) return null;
        return <AccessDenied to={homePath} />;
      }}
    />
  );
}
